import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import type { TrackerTag } from '@racemap/sdk/schema/trackerTags';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Badge, Button, ColorPicker, Modal, Popconfirm, Space } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useState } from 'react';
import { EditableTextField } from 'src/components/BasicComponents/EditableTextField';
import { ShareModal } from 'src/components/BasicComponents/ShareModal/ShareModal';
import { IconGear, IconShare, IconTag } from 'src/components/Icon';
import { type Color, formatColorToHexString } from 'src/components/utils/colorPickerUtils';
import { TagAssignedTrackersTable } from './TagAssignedTrackersTable';

interface Props {
  tag: Immutable<TrackerTag>;
  handleTrackersRemove: (trackerId: Array<string>) => void;
  handleTagUpdate: (key: keyof TrackerTag, newValue: string | Array<string>) => Promise<void>;
  handleTagColorChange: (color: string) => Promise<void>;
  removeTrackerTag: (tagId: ObjectId) => Promise<void>;
  shareLink: string;
}

export const TagModal: FC<Props> = ({
  tag,
  handleTrackersRemove,
  handleTagUpdate,
  handleTagColorChange,
  removeTrackerTag,
  shareLink,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<React.Key>>([]);

  const showModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleAction = () => {
    handleTrackersRemove(selectedRowKeys.map(String));
    setSelectedRowKeys([]);
  };

  const [color, setColor] = useState<Color>(tag.color);

  const handleSetColor = (color: Color) => {
    handleTagColorChange(formatColorToHexString(color));
  };

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  return (
    <>
      <Modal
        title={
          <TagIconAndLabel>
            <ColorPicker value={color} onChange={setColor} onChangeComplete={handleSetColor}>
              <TagIcon color={formatColorToHexString(color)} />
            </ColorPicker>
            <EditableTextField
              value={tag.label || ''}
              onSave={(newLabel) => handleTagUpdate('label', newLabel)}
            />
          </TagIconAndLabel>
        }
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <ButtonGroup>
          <Popconfirm
            title="Remove!"
            onConfirm={() => removeTrackerTag(tag.id)}
            description={'Are you sure you want to delete the tag?'}
          >
            <Button danger>Delete Tag</Button>
          </Popconfirm>
          <DeleteAndShareButton>
            <Button onClick={handleAction} disabled={!selectedRowKeys.length}>
              <Space>
                {selectedRowKeys.length > 0 && (
                  <Badge count={selectedRowKeys.length} color={`${RacemapColors.PaleBlue}cc`} />
                )}
                Remove Tracker(s)
              </Space>
            </Button>
            <Button onClick={() => setIsShareModalOpen(true)} icon={<IconShare color="black" />} />
          </DeleteAndShareButton>
        </ButtonGroup>
        <TagAssignedTrackersTable
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          tag={tag}
          handleTrackersRemove={handleTrackersRemove}
        />
      </Modal>
      <GearIcon className="toolbar" onClick={showModal} />
      <ShareModal
        link={shareLink}
        description="Hey, I wanted to share these trackers with you! Check them out using the link below."
        isOpen={isShareModalOpen}
        onCancel={() => setIsShareModalOpen(false)}
      />
    </>
  );
};

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start; 
  margin-top: 10px;
  margin-bottom: 10px;
`;

const DeleteAndShareButton = styled.div`
    margin-left: auto; 
    display: flex;
    gap: 10px;
`;

const TagIconAndLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;

  .toolbar {
    font-size: 14px;
  }
`;

const TagIcon = styled(IconTag)`
  cursor: pointer;
  padding: 4px;
  border: 1px solid ${RacemapColors.Gray};
  border-radius: 10%;
    &:hover {
      border: 1px solid ${RacemapColors.PaleBlue};
      background-color: ${RacemapColors.CloudBlue};
    }
`;

const GearIcon = styled(IconGear)`
  cursor: pointer;
  display: none;
  color: ${RacemapColors.DarkGray};
`;
